import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, Link } from 'react-router-dom';
import { BookIcon, ArrowLeftIcon, CheckCircleIcon, XIcon, InfoIcon, LightbulbIcon, ArrowRightIcon, BookmarkIcon, ShareIcon, HeadphonesIcon, ExternalLinkIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import { Sitemap } from '../components/Sitemap';
import { NextPage } from '../components/NextPage';
import { Tooltip } from '../components/ui/Tooltip';
import { useToast } from '../components/ui/ToastContainer';
const GRAMMAR_TOPICS = {
  'present-tense': {
    id: 'present-tense',
    title: {
      en: 'Present Tense',
      ar: 'زمن المضارع'
    },
    description: {
      en: 'Learn how to talk about habits and current actions',
      ar: 'تعلم كيفية التحدث عن العادات والأفعال الحالية'
    },
    introduction: {
      en: 'The present tense is used to talk about habits, general truths, scheduled events, and actions happening now. In English, there are several forms of the present tense, including the simple present and present continuous.',
      ar: 'يستخدم زمن المضارع للتحدث عن العادات والحقائق العامة والأحداث المجدولة والأفعال التي تحدث الآن. في اللغة الإنجليزية، هناك عدة أشكال لزمن المضارع، بما في ذلك المضارع البسيط والمضارع المستمر.'
    },
    rules: [{
      title: {
        en: 'Simple Present',
        ar: 'المضارع البسيط'
      },
      explanation: {
        en: 'We use the simple present tense for habits, routines, facts, and general truths.',
        ar: 'نستخدم زمن المضارع البسيط للعادات والروتين والحقائق والحقائق العامة.'
      },
      examples: [{
        en: 'I study English every day.',
        ar: 'أدرس اللغة الإنجليزية كل يوم.'
      }, {
        en: 'She works in a bank.',
        ar: 'هي تعمل في بنك.'
      }, {
        en: 'Water boils at 100 degrees Celsius.',
        ar: 'يغلي الماء عند 100 درجة مئوية.'
      }],
      notes: {
        en: 'For third-person singular (he/she/it), add -s or -es to the verb.',
        ar: 'بالنسبة للشخص الثالث المفرد (هو/هي/هذا)، أضف -s أو -es إلى الفعل.'
      }
    }, {
      title: {
        en: 'Present Continuous',
        ar: 'المضارع المستمر'
      },
      explanation: {
        en: 'We use the present continuous for actions happening now or around now, and for temporary situations.',
        ar: 'نستخدم المضارع المستمر للأفعال التي تحدث الآن أو حول الآن، وللمواقف المؤقتة.'
      },
      examples: [{
        en: 'I am learning English now.',
        ar: 'أنا أتعلم اللغة الإنجليزية الآن.'
      }, {
        en: "She's working from home this week.",
        ar: 'هي تعمل من المنزل هذا الأسبوع.'
      }],
      notes: {
        en: 'Form: am/is/are + verb-ing',
        ar: 'الصيغة: am/is/are + الفعل مع إضافة ing'
      }
    }],
    practice: {
      title: {
        en: 'Practice Exercises',
        ar: 'تمارين تطبيقية'
      },
      exercises: [{
        question: {
          en: 'She _____ (work) in a hospital.',
          ar: 'هي _____ (تعمل) في مستشفى.'
        },
        answer: 'works',
        options: ['work', 'works', 'working', 'is work']
      }, {
        question: {
          en: 'They _____ (study) English at the moment.',
          ar: 'هم _____ (يدرسون) ��لإنجليزية في هذه اللحظة.'
        },
        answer: 'are studying',
        options: ['study', 'studies', 'are studying', 'is studying']
      }, {
        question: {
          en: 'Water _____ (freeze) at 0 degrees Celsius.',
          ar: 'الماء _____ (يتجمد) عند 0 درجة مئوية.'
        },
        answer: 'freezes',
        options: ['freeze', 'freezes', 'freezing', 'is freeze']
      }]
    },
    nextTopic: {
      id: 'past-tense',
      title: {
        en: 'Past Tense',
        ar: 'زمن الماضي'
      }
    }
  },
  'past-tense': {
    id: 'past-tense',
    title: {
      en: 'Past Tense',
      ar: 'زمن الماضي'
    },
    description: {
      en: 'Express completed actions and past experiences',
      ar: 'التعبير عن الأفعال المكتملة والتجارب الماضية'
    },
    introduction: {
      en: 'The past tense is used to talk about actions or situations that happened in the past. In English, there are several forms of the past tense, including the simple past and past continuous.',
      ar: 'يستخدم زمن الماضي للتحدث عن الأفعال أو المواقف التي حدثت في الماضي. في اللغة الإنجليزية، هناك عدة أشكال لزمن الماضي، بما في ذلك الماضي البسيط والماضي المستمر.'
    },
    rules: [{
      title: {
        en: 'Simple Past',
        ar: 'الماضي البسيط'
      },
      explanation: {
        en: 'We use the simple past tense for completed actions in the past.',
        ar: 'نستخدم زمن الماضي البسيط للأفعال المكتملة في الماضي.'
      },
      examples: [{
        en: 'I visited Paris last year.',
        ar: 'زرت باريس العام الماضي.'
      }, {
        en: 'She studied English for two hours yesterday.',
        ar: 'درست اللغة الإنجليزية لمدة ساعتين أمس.'
      }],
      notes: {
        en: 'Regular verbs: add -ed. Irregular verbs have special forms.',
        ar: 'الأفعال المنتظمة: أضف -ed. الأفعال غير المنتظمة لها أشكال خاصة.'
      }
    }],
    practice: {
      title: {
        en: 'Practice Exercises',
        ar: 'تمارين تطبيقية'
      },
      exercises: [{
        question: {
          en: 'I _____ (visit) my grandmother last weekend.',
          ar: 'أنا _____ (زرت) جدتي نهاية الأسبوع الماضي.'
        },
        answer: 'visited',
        options: ['visit', 'visits', 'visited', 'visiting']
      }, {
        question: {
          en: 'They _____ (go) to the beach last summer.',
          ar: 'هم _____ (ذهبوا) إلى الشاطئ الصيف الماضي.'
        },
        answer: 'went',
        options: ['go', 'goes', 'went', 'going']
      }]
    },
    nextTopic: {
      id: 'future-tense',
      title: {
        en: 'Future Tense',
        ar: 'زمن المستقبل'
      }
    }
  },
  'future-tense': {
    id: 'future-tense',
    title: {
      en: 'Future Tense',
      ar: 'زمن المستقبل'
    },
    description: {
      en: 'Discuss plans and make predictions',
      ar: 'مناقشة الخطط وتقديم التنبؤات'
    },
    introduction: {
      en: 'The future tense is used to talk about actions or situations that will happen in the future. In English, there are several ways to express the future.',
      ar: 'يستخدم زمن المستقبل للتحدث عن الأفعال أو المواقف التي ستحدث في المستقبل. في اللغة الإنجليزية، هناك عدة طرق للتعبير عن المستقبل.'
    },
    rules: [{
      title: {
        en: 'Future with "will"',
        ar: 'المستقبل مع "will"'
      },
      explanation: {
        en: 'We use "will" for predictions, spontaneous decisions, promises, and offers.',
        ar: 'نستخدم "will" للتنبؤات والقرارات العفوية والوعود والعروض.'
      },
      examples: [{
        en: 'I think it will rain tomorrow.',
        ar: 'أعتقد أنها ستمطر غداً.'
      }, {
        en: "I'll help you with your homework.",
        ar: 'سأساعدك في واجباتك المنزلية.'
      }],
      notes: {
        en: 'Form: will + base form of the verb',
        ar: 'الصيغة: will + الشكل الأساسي للفعل'
      }
    }],
    practice: {
      title: {
        en: 'Practice Exercises',
        ar: 'تمارين تطبيقية'
      },
      exercises: [{
        question: {
          en: 'I _____ (call) you tomorrow.',
          ar: 'أنا _____ (سأتصل) بك غداً.'
        },
        answer: 'will call',
        options: ['call', 'calls', 'will call', 'calling']
      }, {
        question: {
          en: 'She _____ (be) at the meeting next week.',
          ar: 'هي _____ (ستكون) في الاجتماع الأسبوع القادم.'
        },
        answer: 'will be',
        options: ['is', 'was', 'will be', 'being']
      }]
    },
    nextTopic: null
  }
};
export function GrammarTopicPage() {
  const {
    isArabic
  } = useLanguage();
  const {
    topicId
  } = useParams<{
    topicId: string;
  }>();
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [helpfulFeedback, setHelpfulFeedback] = useState<boolean | null>(null);
  const toast = useToast();
  const topic = GRAMMAR_TOPICS[topicId as keyof typeof GRAMMAR_TOPICS] || GRAMMAR_TOPICS['present-tense'];
  useEffect(() => {
    setUserAnswers({});
    setShowResults(false);
    setHelpfulFeedback(null);
    window.scrollTo(0, 0);
  }, [topicId]);
  const handleAnswerSelect = (exerciseIndex: number, answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [exerciseIndex]: answer
    }));
  };
  const checkAnswers = () => {
    setShowResults(true);
    const score = correctAnswers / topic.practice.exercises.length;
    if (score === 1) {
      toast.success(isArabic ? 'أحسنت!' : 'Perfect score!', isArabic ? 'لقد أجبت على جميع الأسئلة بشكل صحيح.' : 'You answered all questions correctly.');
    } else if (score >= 0.7) {
      toast.success(isArabic ? 'عمل جيد!' : 'Good job!', isArabic ? `لقد أجبت على ${correctAnswers} من أصل ${topic.practice.exercises.length} بشكل صحيح.` : `You got ${correctAnswers} out of ${topic.practice.exercises.length} correct.`);
    } else {
      toast.info(isArabic ? 'استمر في المحاولة' : 'Keep practicing', isArabic ? `لقد أجبت على ${correctAnswers} من أصل ${topic.practice.exercises.length} بشكل صحيح.` : `You got ${correctAnswers} out of ${topic.practice.exercises.length} correct.`);
    }
  };
  const resetExercise = () => {
    setUserAnswers({});
    setShowResults(false);
  };
  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    if (!isBookmarked) {
      toast.success(isArabic ? 'تمت الإضافة للمفضلة' : 'Added to bookmarks', isArabic ? 'يمكنك العثور على هذا الموضوع في قسم المفضلة.' : 'You can find this topic in your bookmarks section.');
    } else {
      toast.info(isArabic ? 'تمت الإزالة من المفضلة' : 'Removed from bookmarks');
    }
  };
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: isArabic ? topic.title.ar : topic.title.en,
        text: isArabic ? topic.description.ar : topic.description.en,
        url: window.location.href
      }).catch(error => {
        console.error('Error sharing:', error);
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success(isArabic ? 'تم نسخ الرابط' : 'Link copied', isArabic ? 'تم نسخ رابط الموضوع إلى الحافظة.' : 'Topic link copied to clipboard.');
      });
    }
  };
  const provideFeedback = (isHelpful: boolean) => {
    setHelpfulFeedback(isHelpful);
    if (isHelpful) {
      toast.success(isArabic ? 'شكراً لملاحظاتك' : 'Thank you for your feedback', isArabic ? 'نحن سعداء لأن هذا المحتوى كان مفيداً لك.' : "We're glad this content was helpful to you.");
    } else {
      toast.info(isArabic ? 'شكراً لمل��حظاتك' : 'Thank you for your feedback', isArabic ? 'سنعمل على تحسين هذا المحتوى.' : "We'll work on improving this content.");
    }
  };
  const playAudio = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };
  const correctAnswers = topic.practice.exercises.reduce((count, exercise, index) => {
    return userAnswers[index] === exercise.answer ? count + 1 : count;
  }, 0);
  const nextTopicUrl = topic.nextTopic ? `/resources/grammar/${topic.nextTopic.id}` : '/resources/grammar';
  const currentTopicIndex = Object.keys(GRAMMAR_TOPICS).indexOf(topic.id);
  const totalTopics = Object.keys(GRAMMAR_TOPICS).length;
  const relatedTopics = Object.values(GRAMMAR_TOPICS).filter(t => t.id !== topic.id).slice(0, 2);
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2900">
      <Sitemap currentPage={{
      en: topic.title.en,
      ar: topic.title.ar
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }, {
      label: {
        en: 'Grammar Guide',
        ar: 'دليل القواعد'
      },
      href: '/resources/grammar'
    }]} data-id="element-2901" />
      <div className="max-w-7xl mx-auto" data-id="element-2902">
        <div className="mb-12" data-id="element-2903">
          <div className="flex items-center justify-between mb-4" data-id="element-2904">
            <div className="flex items-center gap-3" data-id="element-2905">
              <BookIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" data-id="element-2906" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white" data-id="element-2907">
                {isArabic ? topic.title.ar : topic.title.en}
              </h1>
            </div>
            <div className="flex items-center gap-2" data-id="element-2908">
              <Tooltip content={isArabic ? 'إضافة للمفضلة' : 'Add to bookmarks'} data-id="element-2909">
                <button onClick={toggleBookmark} className={`p-2 rounded-full transition-colors ${isBookmarked ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'}`} aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'} data-id="element-2910">
                  <BookmarkIcon className="w-5 h-5" data-id="element-2911" />
                </button>
              </Tooltip>
              <Tooltip content={isArabic ? 'مشاركة' : 'Share'} data-id="element-2912">
                <button onClick={handleShare} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors" aria-label="Share" data-id="element-2913">
                  <ShareIcon className="w-5 h-5" data-id="element-2914" />
                </button>
              </Tooltip>
            </div>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-2915">
            {isArabic ? topic.description.ar : topic.description.en}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-2916">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2917">
            <div data-id="element-2918">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2919">
                {isArabic ? 'تقدمك في هذا الموضوع' : 'Your progress on this topic'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2920">
                {isArabic ? 'أكمل التمارين أدناه لتتبع تقدمك' : 'Complete the exercises below to track your progress'}
              </p>
            </div>
            <div className="w-full sm:w-48" data-id="element-2921">
              <div className="flex items-center justify-between text-sm mb-1" data-id="element-2922">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-2923">
                  {showResults ? `${Math.round(correctAnswers / topic.practice.exercises.length * 100)}%` : '0%'}
                </span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-2924">
                  {isArabic ? 'مكتمل' : 'Complete'}
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2925">
                <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
                width: showResults ? `${Math.round(correctAnswers / topic.practice.exercises.length * 100)}%` : '0%'
              }} data-id="element-2926" />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-2927">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2928">
            {isArabic ? 'مقدمة' : 'Introduction'}
          </h2>
          <p className="text-gray-700 dark:text-gray-300" data-id="element-2929">
            {isArabic ? topic.introduction.ar : topic.introduction.en}
          </p>
        </div>
        <div className="space-y-8 mb-12" data-id="element-2930">
          {topic.rules.map((rule, index) => <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2931">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2932">
                {isArabic ? rule.title.ar : rule.title.en}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-2933">
                {isArabic ? rule.explanation.ar : rule.explanation.en}
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6" data-id="element-2934">
                <div className="flex items-start gap-3 mb-4" data-id="element-2935">
                  <LightbulbIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-2936" />
                  <p className="text-gray-800 dark:text-gray-200 font-medium" data-id="element-2937">
                    {isArabic ? rule.notes.ar : rule.notes.en}
                  </p>
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3" data-id="element-2938">
                {isArabic ? 'أمثلة' : 'Examples'}
              </h3>
              <div className="space-y-3 mb-4" data-id="element-2939">
                {rule.examples.map((example, i) => <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between group hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2940">
                    <div className="flex items-center gap-3" data-id="element-2941">
                      <span className="text-gray-900 dark:text-white font-medium" data-id="element-2942">
                        {example.en}
                      </span>
                      <Tooltip content={isArabic ? 'استمع إلى النطق' : 'Listen to pronunciation'} data-id="element-2943">
                        <button onClick={() => playAudio(example.en)} className="p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-all" aria-label="Play pronunciation" data-id="element-2944">
                          <HeadphonesIcon className="w-4 h-4" data-id="element-2945" />
                        </button>
                      </Tooltip>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 mt-2 md:mt-0" dir="rtl" data-id="element-2946">
                      {example.ar}
                    </span>
                  </div>)}
              </div>
            </div>)}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-12" data-id="element-2947">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2948">
            {isArabic ? topic.practice.title.ar : topic.practice.title.en}
          </h2>
          <div className="space-y-8" data-id="element-2949">
            {topic.practice.exercises.map((exercise, index) => <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6" data-id="element-2950">
                <p className="text-lg text-gray-900 dark:text-white mb-4" data-id="element-2951">
                  {isArabic ? exercise.question.ar : exercise.question.en}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2952">
                  {exercise.options.map(option => <button key={option} onClick={() => handleAnswerSelect(index, option)} disabled={showResults} className={`
                        p-3 rounded-lg border text-left transition-all
                        ${userAnswers[index] === option ? showResults ? option === exercise.answer ? 'bg-green-100 dark:bg-green-900/30 border-green-500 dark:border-green-500' : 'bg-red-100 dark:bg-red-900/30 border-red-500 dark:border-red-500' : 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 dark:border-blue-500 ring-2 ring-blue-500 dark:ring-blue-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}
                        ${!showResults && !userAnswers[index] ? 'hover:border-blue-300 dark:hover:border-blue-600' : ''}
                      `} aria-pressed={userAnswers[index] === option} data-id="element-2953">
                      {option}
                      {showResults && userAnswers[index] === option && (option === exercise.answer ? <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400 float-right" data-id="element-2954" /> : <XIcon className="w-5 h-5 text-red-500 dark:text-red-400 float-right" data-id="element-2955" />)}
                      {showResults && option === exercise.answer && userAnswers[index] !== option && <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400 float-right" data-id="element-2956" />}
                    </button>)}
                </div>
                {showResults && <div className={`mt-4 p-3 rounded-lg text-sm ${userAnswers[index] === exercise.answer ? 'bg-green-50 dark:bg-green-900/10 text-green-800 dark:text-green-300' : 'bg-blue-50 dark:bg-blue-900/10 text-blue-800 dark:text-blue-300'}`} data-id="element-2957">
                    <p className="font-medium mb-1" data-id="element-2958">
                      {userAnswers[index] === exercise.answer ? isArabic ? 'إجابة صحيحة!' : 'Correct answer!' : isArabic ? 'الإ��ابة الصحيحة:' : 'Correct answer:'}{' '}
                      {exercise.answer}
                    </p>
                    <p data-id="element-2959">
                      {userAnswers[index] !== exercise.answer && exercise.explanation && (isArabic ? exercise.explanation.ar : exercise.explanation.en)}
                    </p>
                  </div>}
              </div>)}
          </div>
          {showResults ? <div className="mt-8" data-id="element-2960">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-6" data-id="element-2961">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2962">
                  {isArabic ? 'نتائجك' : 'Your Results'}
                </h3>
                <p className="text-gray-700 dark:text-gray-300" data-id="element-2963">
                  {isArabic ? `لقد أجبت على ${correctAnswers} من أصل ${topic.practice.exercises.length} بشكل صحيح.` : `You got ${correctAnswers} out of ${topic.practice.exercises.length} correct.`}
                </p>
              </div>
              <button onClick={resetExercise} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2964">
                {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
              </button>
            </div> : <button onClick={checkAnswers} disabled={Object.keys(userAnswers).length < topic.practice.exercises.length} className={`
                mt-6 px-6 py-2 rounded-lg transition-all
                ${Object.keys(userAnswers).length < topic.practice.exercises.length ? 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:-translate-y-0.5'}
              `} data-id="element-2965">
              {isArabic ? 'تحقق من إجاباتك' : 'Check Your Answers'}
            </button>}
        </div>
        <div className="mb-12" data-id="element-2966">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2967">
            {isArabic ? 'مواضيع ذات صلة' : 'Related Topics'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2968">
            {relatedTopics.map(relatedTopic => <Link key={relatedTopic.id} to={`/resources/grammar/${relatedTopic.id}`} className="group bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-6 flex items-start gap-4" data-id="element-2969">
                <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30" data-id="element-2970">
                  <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2971" />
                </div>
                <div data-id="element-2972">
                  <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-2973">
                    {isArabic ? relatedTopic.title.ar : relatedTopic.title.en}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mt-1" data-id="element-2974">
                    {isArabic ? relatedTopic.description.ar : relatedTopic.description.en}
                  </p>
                </div>
              </Link>)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-12" data-id="element-2975">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3" data-id="element-2976">
            {isArabic ? 'هل كان هذا المحتوى مفيداً؟' : 'Was this content helpful?'}
          </h3>
          <div className="flex items-center gap-4" data-id="element-2977">
            <button onClick={() => provideFeedback(true)} disabled={helpfulFeedback !== null} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${helpfulFeedback === true ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-2978">
              <ThumbsUpIcon className="w-5 h-5" data-id="element-2979" />
              <span data-id="element-2980">
                {isArabic ? 'نعم، كان مفيداً' : 'Yes, it was helpful'}
              </span>
            </button>
            <button onClick={() => provideFeedback(false)} disabled={helpfulFeedback !== null} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${helpfulFeedback === false ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-2981">
              <ThumbsDownIcon className="w-5 h-5" data-id="element-2982" />
              <span data-id="element-2983">
                {isArabic ? 'لا، يحتاج للتحسين' : 'No, needs improvement'}
              </span>
            </button>
          </div>
          {helpfulFeedback === false && <div className="mt-4" data-id="element-2984">
              <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1" data-id="element-2985">
                <span data-id="element-2986">
                  {isArabic ? 'أخبرنا كيف يمكننا التحسين' : 'Tell us how we can improve'}
                </span>
                <ExternalLinkIcon className="w-4 h-4" data-id="element-2987" />
              </a>
            </div>}
        </div>
        <NextPage currentPage={currentTopicIndex + 1} totalPages={totalTopics} nextPageUrl={nextTopicUrl} prevPageUrl="/resources/grammar" nextTitle={topic.nextTopic ? topic.nextTopic.title : {
        en: 'Grammar Guide',
        ar: 'دليل القواعد'
      }} nextDescription={topic.nextTopic ? {
        en: 'Continue learning with the next grammar topic',
        ar: 'واصل التعلم مع موضوع القواعد التالي'
      } : {
        en: 'Return to the grammar guide',
        ar: 'العودة إلى دليل القواعد'
      }} isLastPage={!topic.nextTopic} data-id="element-2988" />
      </div>
    </div>;
}